import { useEffect, useState } from "react";
import styled from "styled-components";
import { gql, useQuery, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { captureException } from "@sentry/react";
import { isEmail } from "validator";
import { createSelector } from "reselect";
import { useSelector } from "react-redux";

import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import LoadingBox from "components/other/LoadingBox";
import BoxLoadingSpinner from "components/other/BoxLoadingSpinner";
import Layout from "components/Layout";
import PageTitle from "components/text/PageTitle";
import RoundActionIcon from "components/buttons/RoundActionIcon";
import Backdrop from "components/other/Backdrop";
import MessageWindow from "components/other/MessageWindow";

import returnIcon from "assets/img/icons/return-icon.svg";
import CreateMailingList from "./CreateMailingList";

const ModalWrap = styled.div`
  width: 100%;
  margin: 0;
  padding: 0;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BackIconWrap = styled.div``;

const CreateMailingListContainer = styled.div`
  position: relative;
  width: 100%;
  box-sizing: border-box;

  margin: 0 0 50px;
  padding: 0 28px;

  display: flex;
  flex-direction: column;

  @media (max-width: ${globalConstants.switchTablet}) {
    margin: 0;
    padding: 0;
  }
`;

export default () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // set page Title
  useEffect(() => {
    document.title = "Create Mailing List | Scalelup";
  }, []);

  const listId = searchParams.get("id") ? searchParams.get("id") : null;

  const [formEdited, setFormEdited] = useState(false);
  const [name, setName] = useState("");
  const [behalfName, setBehalfName] = useState("");
  const [email, setEmail] = useState("");
  const [behalfAddress, setBehalfAddress] = useState({
    street: "",
    city: "",
    zipCode: "",
    country: "",
  });
  const [warnings, setWarnings] = useState([]);
  const [signature, setSignature] = useState("");
  const [pickerOpened, setPickerOpened] = useState(false);
  const [agreeAddress, setAgreeAddress] = useState(false);
  const [agreeConditions, setAgreeConditions] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errorWindow, setErrorWindow] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [emailFormOpened, setEmailFormOpened] = useState(false);

  const UserProperties = createSelector(
    (state) => state.User,
    (user) => ({
      profileData: user.profileData,
    })
  );

  const { profileData } = useSelector(UserProperties);

  const returnPath = listId ? `/mailing-list?id=${listId}` : "/mailing-lists";

  // handle List name input
  const onNameInputChange = (e) => {
    setName(e.target.value);
    if (!formEdited) setFormEdited(true);
  };

  // handle Behalf name input
  const onBehalfNameInputChange = (e) => {
    setBehalfName(e.target.value);
    if (!formEdited) setFormEdited(true);
  };

  // handle Email selection
  const changeEmail = (email) => {
    setEmail(email);
  };

  // handle Behalf address input
  const onBehalfAddressInputChange = (e) => {
    const newAddress = { ...behalfAddress };

    newAddress[e.target.name] = e.target.value;

    setBehalfAddress(newAddress);
    if (!formEdited) setFormEdited(true);
  };

  // handle Behalf country input
  const onAccountCountryChange = (country) => {
    const newAddress = { ...behalfAddress, country: country };

    setBehalfAddress(newAddress);
    if (!formEdited) setFormEdited(true);
  };

  // handle Signature input
  const onSignatureInputChange = (e) => {
    setSignature(e.target.value);
    if (!formEdited) setFormEdited(true);
  };

  const onSmileyClick = () => {
    setPickerOpened(!pickerOpened);
  };

  const onEmojiClick = (emoji) => {
    setSignature(signature + emoji.emoji);

    if (!formEdited) setFormEdited(true);
  };

  function usePickerOutsideClick(ref) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          setPickerOpened(false);
        }
      }

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  // handle agreements
  const onAgreeAddressClick = () => {
    setAgreeAddress((prevState) => !prevState);
  };

  const onAgreeConditionsClick = () => {
    setAgreeConditions((prevState) => !prevState);
  };

  // handle Create/update list
  const SUBMIT_MAILING_LIST = gql`
    mutation SubmitMailingList(
      $listId: ID
      $name: String!
      $behalfName: String!
      $email: String!
      $street: String!
      $city: String!
      $zipCode: String!
      $country: String!
      $signature: String
    ) {
      submitMailingList(
        listId: $listId
        name: $name
        behalfName: $behalfName
        email: $email
        street: $street
        city: $city
        zipCode: $zipCode
        country: $country
        signature: $signature
      ) {
        resultCode
      }
    }
  `;

  const [submitMailingList] = useMutation(SUBMIT_MAILING_LIST);

  const submitForm = async () => {
    try {
      if (submitLoading) return;

      let newWarnings = [];

      if (name.length < 1) newWarnings.push("name");
      if (behalfName.length < 3) newWarnings.push("behalfName");
      if (
        email.trim().length < 5 ||
        email.trim().length > 100 ||
        !isEmail(email)
      ) {
        newWarnings.push("email");
      }
      if (behalfAddress.street.length < 2) newWarnings.push("street");
      if (behalfAddress.city.length < 2) newWarnings.push("city");
      if (behalfAddress.zipCode.length < 2) newWarnings.push("zipCode");
      if (behalfAddress.country.length < 1) newWarnings.push("country");
      if (!agreeAddress) newWarnings.push("agreeAddress");
      if (!agreeConditions) newWarnings.push("agreeConditions");

      if (newWarnings.length !== 0) {
        setWarnings(newWarnings);
        return;
      }

      setWarnings([]);
      setSubmitLoading(true);
      setFormEdited(false);
      setErrorWindow(false);
      setErrorMessage(null);

      const result = await submitMailingList({
        variables: {
          listId,
          name,
          behalfName,
          email,
          street: behalfAddress.street,
          city: behalfAddress.city,
          zipCode: behalfAddress.zipCode,
          country: behalfAddress.country,
          signature,
        },
      });

      console.log("result", result);

      setSubmitLoading(false);

      if (result.data.submitMailingList.resultCode === 4) {
        setErrorMessage(
          "You can have a maximum of 30 mailing lists. Please delete an older list before creating a new one."
        );
        setErrorWindow(true);
        return;
      }

      if (result.data.submitMailingList.resultCode !== 0) {
        setErrorWindow(true);
        return;
      }

      navigate(returnPath);
    } catch (error) {
      captureException(error);
      setErrorWindow(true);
      console.log("submitForm error", error);
    }
  };

  // handle Email form
  const openEmailForm = () => {
    setEmailFormOpened(true);
  };

  const closeEmailForm = () => {
    setEmailFormOpened(false);
  };

  // Error handlig
  const openErrorWindow = () => {
    setErrorWindow(true);
  };

  const handleModalClose = (modal) => {
    if (modal === "error") {
      setErrorWindow(false);
    }
  };

  // Fetch data
  const GET_MAILING_LIST_DATA = gql`
    query GetMailingListData($listId: ID!) {
      mailingList(listId: $listId) {
        id
        name
        behalf_name
        behalf_email
        behalf_street
        behalf_city
        behalf_zip
        behalf_country
        signature
      }
    }
  `;

  const { loading, data, error } = useQuery(GET_MAILING_LIST_DATA, {
    variables: {
      listId,
    },
    skip: !listId,
    fetchPolicy: "network-only",
  });

  if (error) {
    console.log("error fetching mailing list data", error);
    captureException(error);
  }

  useEffect(() => {
    if (!loading && data) {
      if (!data.mailingList) {
        // list not found
        navigate("/mailing-lists");
        return;
      }

      // populate state with existing list data
      const {
        name,
        behalf_name,
        behalf_email,
        behalf_street,
        behalf_city,
        behalf_zip,
        behalf_country,
        signature,
      } = data.mailingList;

      if (name) setName(name);
      if (behalf_name) setBehalfName(behalf_name);
      if (behalf_email) setEmail(behalf_email);
      if (behalf_street)
        setBehalfAddress({
          street: behalf_street,
          city: behalf_city,
          zipCode: behalf_zip,
          country: behalf_country,
        });

      if (signature) setSignature(signature);

      setFormEdited(false);
    }
  }, [loading]);

  useEffect(() => {
    if (!listId) {
      setEmail(profileData.email);
    }
  }, []);

  console.log("data", data);

  const title = listId ? "Edit Mailing List" : "Create Mailing List";

  return (
    <Layout>
      <CreateMailingListContainer>
        <Header>
          <PageTitle>{title}</PageTitle>
          <BackIconWrap>
            <RoundActionIcon
              icon={returnIcon}
              iconSize="16px"
              iconAlt="Return"
              onClick={() => navigate(returnPath)}
            />
          </BackIconWrap>
        </Header>
        {/* {!loading && (
          <LoadingBox>
            <BoxLoadingSpinner height="435px" />
          </LoadingBox>
        )} */}
        <CreateMailingList
          listId={listId}
          name={name}
          onNameInputChange={onNameInputChange}
          behalfName={behalfName}
          onBehalfNameInputChange={onBehalfNameInputChange}
          email={email}
          behalfAddress={behalfAddress}
          onBehalfAddressInputChange={onBehalfAddressInputChange}
          onAccountCountryChange={onAccountCountryChange}
          warnings={warnings}
          signature={signature}
          onSignatureInputChange={onSignatureInputChange}
          onSmileyClick={onSmileyClick}
          pickerOpened={pickerOpened}
          onEmojiClick={onEmojiClick}
          usePickerOutsideClick={usePickerOutsideClick}
          agreeAddress={agreeAddress}
          onAgreeAddressClick={onAgreeAddressClick}
          agreeConditions={agreeConditions}
          onAgreeConditionsClick={onAgreeConditionsClick}
          submitForm={submitForm}
          submitLoading={submitLoading}
          openEmailForm={openEmailForm}
          emailFormOpened={emailFormOpened}
          closeEmailForm={closeEmailForm}
          openErrorWindow={openErrorWindow}
          changeEmail={changeEmail}
        />
        {errorWindow && (
          <ModalWrap>
            <Backdrop
              onClick={handleModalClose}
              parameters="error"
              background="rgba(0, 0, 0, 0.7)"
            />
            <MessageWindow handleModalClose={handleModalClose} type="error">
              {errorMessage}
            </MessageWindow>
          </ModalWrap>
        )}
      </CreateMailingListContainer>
    </Layout>
  );
};
